/**
 * /coach/assessment/:sessionId — the active assessment screen.
 *
 * Today this only handles **launch + participants** (start, resume, walk-ins,
 * remove, cancel). Test selection + results + email will follow in the next
 * sprint.
 *
 * Auto-saves notes on blur. Drafts persist server-side; closing the tab
 * never loses anything.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import TestSelectionPanel from "@/components/TestSelectionPanel";
import AssessmentRecapModal from "@/components/AssessmentRecapModal";
import ParentLiveLinkModal from "@/components/coach/ParentLiveLinkModal";
import {
    ArrowLeft,
    Loader2,
    Users,
    UserPlus,
    Trash2,
    AlertTriangle,
    Save,
    X,
    Activity,
    Send,
    QrCode,
    RotateCcw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AssessmentSession() {
    const { sessionId: routeId } = useParams();
    const [search] = useSearchParams();
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();

    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showWalkIn, setShowWalkIn] = useState(false);
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [showRecap, setShowRecap] = useState(false);
    const [recapMode, setRecapMode] = useState("send"); // "send" | "resend"
    const [liveLinkParent, setLiveLinkParent] = useState(null); // {uid, name} or null
    const [notesDraft, setNotesDraft] = useState("");
    const [savingNotes, setSavingNotes] = useState(false);

    // -------- Auth gate
    useEffect(() => {
        if (!authLoading && (!user || !["owner", "coach"].includes(user.role))) {
            navigate("/", { replace: true });
        }
    }, [authLoading, user, navigate]);

    // -------- Initial load OR auto-create-from-booking
    useEffect(() => {
        if (authLoading) return;
        let cancelled = false;
        (async () => {
            try {
                if (routeId === "new") {
                    const bookingId = search.get("booking_id");
                    const requestId = search.get("request_id");
                    const body = {};
                    if (bookingId) body.booking_id = bookingId;
                    if (requestId) body.request_id = requestId;
                    const r = await axios.post(`${API}/coach/assessment-sessions`, body, { withCredentials: true });
                    if (!cancelled) {
                        navigate(`/coach/assessment/${r.data.session_id}`, { replace: true });
                    }
                } else {
                    const r = await axios.get(`${API}/coach/assessment-sessions/${routeId}`, { withCredentials: true });
                    if (!cancelled) {
                        setSession(r.data);
                        setNotesDraft(r.data?.notes || "");
                    }
                }
            } catch (e) {
                toast.error("Couldn't load assessment", { description: e?.response?.data?.detail });
                navigate("/coach", { replace: true });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeId, authLoading]);

    const refresh = useCallback(async () => {
        const r = await axios.get(`${API}/coach/assessment-sessions/${routeId}`, { withCredentials: true });
        setSession(r.data);
    }, [routeId]);

    const removeParticipant = async (athleteId) => {
        try {
            const r = await axios.delete(
                `${API}/coach/assessment-sessions/${routeId}/participants/${athleteId}`,
                { withCredentials: true },
            );
            setSession(r.data);
            toast.success("Participant removed");
        } catch (e) {
            toast.error("Couldn't remove", { description: e?.response?.data?.detail });
        }
    };

    const togglePresence = async (athleteId, next) => {
        // Optimistic update — flip the checkbox immediately for snappy UX.
        setSession((prev) => prev ? {
            ...prev,
            participants: (prev.participants || []).map((p) =>
                p.athlete_id === athleteId ? { ...p, included_today: next } : p,
            ),
        } : prev);
        try {
            const r = await axios.patch(
                `${API}/coach/assessment-sessions/${routeId}/participants/${athleteId}/presence`,
                { included_today: next },
                { withCredentials: true },
            );
            setSession(r.data);
        } catch (e) {
            // Roll back the optimistic flip on failure
            setSession((prev) => prev ? {
                ...prev,
                participants: (prev.participants || []).map((p) =>
                    p.athlete_id === athleteId ? { ...p, included_today: !next } : p,
                ),
            } : prev);
            toast.error("Couldn't update", { description: e?.response?.data?.detail });
        }
    };

    const saveNotes = async () => {
        if (notesDraft === (session?.notes || "")) return;
        setSavingNotes(true);
        try {
            const r = await axios.patch(
                `${API}/coach/assessment-sessions/${routeId}`,
                { notes: notesDraft },
                { withCredentials: true },
            );
            setSession(r.data);
        } catch (e) {
            toast.error("Couldn't save notes", { description: e?.response?.data?.detail });
        } finally {
            setSavingNotes(false);
        }
    };

    const cancelSession = async () => {
        try {
            await axios.delete(`${API}/coach/assessment-sessions/${routeId}`, { withCredentials: true });
            toast.success("Assessment cancelled");
            navigate("/coach");
        } catch (e) {
            toast.error("Couldn't cancel", { description: e?.response?.data?.detail });
            setConfirmCancel(false);
        }
    };

    if (loading || authLoading || !session) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <Loader2 className="w-7 h-7 animate-spin text-brand-red" />
            </div>
        );
    }

    const isCompleted = session.status !== "draft";
    const participants = session.participants || [];

    return (
        <div data-testid="assessment-session" className="min-h-screen bg-brand-obsidian text-white">
            <section className="pt-28 pb-6 border-b border-zinc-900">
                <div className="max-w-4xl mx-auto px-6 md:px-10">
                    <button
                        type="button"
                        onClick={() => navigate("/coach")}
                        className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 hover:text-brand-red inline-flex items-center gap-1.5 mb-5"
                    >
                        <ArrowLeft className="w-3 h-3" /> Coach console
                    </button>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-2 inline-flex items-center gap-3">
                                <span className="inline-block w-10 h-px bg-brand-red" />
                                Assessment · {session.status === "draft" ? "Draft" : session.status}
                            </div>
                            <h1 className="font-display text-4xl md:text-5xl tracking-wider uppercase">
                                {participants.length === 0 && "Empty assessment"}
                                {participants.length === 1 && participants[0].name}
                                {participants.length > 1 && `${participants[0].name.split(" ")[0]} +${participants.length - 1}`}
                            </h1>
                            <p className="mt-3 text-sm text-zinc-300">
                                Started by {session.started_by_name} ·{" "}
                                {session.started_at ? new Date(session.started_at).toLocaleString() : ""}
                                {session.booking_id && <> · linked to booking</>}
                            </p>
                        </div>
                        {!isCompleted && (
                            <button
                                type="button"
                                data-testid="cancel-session"
                                onClick={() => setConfirmCancel(true)}
                                className="btn-ghost-tactical text-xs"
                            >
                                <Trash2 className="w-3.5 h-3.5" /> Cancel assessment
                            </button>
                        )}
                    </div>
                </div>
            </section>

            <section className="py-8">
                <div className="max-w-4xl mx-auto px-6 md:px-10 space-y-8">
                    {/* Participants */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red inline-flex items-center gap-2">
                                <Users className="w-3.5 h-3.5" /> Participants ({participants.length})
                            </div>
                            {!isCompleted && (
                                <button
                                    type="button"
                                    data-testid="add-walkin"
                                    onClick={() => setShowWalkIn(true)}
                                    className="btn-ghost-tactical text-xs"
                                >
                                    <UserPlus className="w-3.5 h-3.5" /> Add walk-in
                                </button>
                            )}
                        </div>

                        {participants.length === 0 ? (
                            <div className="border border-zinc-800 rounded-2xl bg-zinc-950/60 p-8 text-center text-zinc-300">
                                <Activity className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                                No participants yet. Add a walk-in to begin.
                            </div>
                        ) : (
                            <>
                                {!isCompleted && (
                                    <div className="mb-3 text-[11px] text-zinc-400 leading-relaxed">
                                        Check off each athlete who actually showed up today. Only checked
                                        athletes are included in the recap email — protects against
                                        accidentally emailing a parent about a sibling who didn't show.
                                    </div>
                                )}
                                <ul className="space-y-2">
                                    {participants.map((p) => {
                                        const present = !!p.included_today;
                                        return (
                                            <li
                                                key={p.athlete_id}
                                                data-testid={`participant-${p.athlete_id}`}
                                                data-present={present ? "true" : "false"}
                                                className={`border rounded-xl px-4 py-3 flex items-center gap-4 transition-colors ${
                                                    present
                                                        ? "border-brand-red/60 bg-zinc-950/80"
                                                        : "border-zinc-800 bg-zinc-950/30 opacity-70"
                                                }`}
                                            >
                                                {!isCompleted && (
                                                    <label
                                                        className="inline-flex items-center gap-2 cursor-pointer select-none shrink-0"
                                                        title="Check if this athlete showed up today"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            data-testid={`presence-${p.athlete_id}`}
                                                            checked={present}
                                                            onChange={(e) => togglePresence(p.athlete_id, e.target.checked)}
                                                            className="w-5 h-5 accent-brand-red"
                                                        />
                                                        <span className={`text-[10px] tracking-[0.18em] uppercase ${present ? "text-brand-red" : "text-zinc-400"}`}>
                                                            {present ? "Here" : "Absent"}
                                                        </span>
                                                    </label>
                                                )}
                                                <div className="w-9 h-9 rounded-full bg-brand-red/15 border border-brand-red/40 flex items-center justify-center shrink-0 text-brand-red font-display text-sm">
                                                    {(p.name || "?")[0].toUpperCase()}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-sm font-semibold text-white truncate">
                                                        {p.name}
                                                        {p.is_walkin && (
                                                            <span className="ml-2 text-[10px] tracking-[0.15em] uppercase text-brand-navy-300 border border-brand-navy-700 bg-brand-navy-900/40 px-1.5 py-0.5">
                                                                walk-in
                                                            </span>
                                                        )}
                                                    </div>
                                                    {p.email && !p.email.endsWith("@maxsc.local") && (
                                                        <div className="text-[11px] text-zinc-300 truncate">{p.email}</div>
                                                    )}
                                                </div>
                                                {!isCompleted && p.parent_user_id && (
                                                    <button
                                                        type="button"
                                                        data-testid={`live-link-${p.athlete_id}`}
                                                        onClick={() => setLiveLinkParent({ uid: p.parent_user_id, name: p.name })}
                                                        title="Show parent's live view QR"
                                                        className="text-zinc-300 hover:text-brand-red mr-1"
                                                    >
                                                        <QrCode className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {!isCompleted && (
                                                    <button
                                                        type="button"
                                                        data-testid={`remove-${p.athlete_id}`}
                                                        onClick={() => removeParticipant(p.athlete_id)}
                                                        title="Remove from this assessment"
                                                        className="text-zinc-300 hover:text-brand-red"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </>
                        )}
                    </div>

                    {/* Per-participant tests — only for athletes the coach has checked in as present today */}
                    {!isCompleted && participants.length > 0 && (() => {
                        const present = participants.filter((p) => p.included_today);
                        if (present.length === 0) {
                            return (
                                <div data-testid="no-present-banner" className="border border-amber-700/40 bg-amber-900/10 rounded-2xl p-4 text-[12px] text-amber-200 leading-relaxed">
                                    Check off who showed up above to start logging tests. Nothing is sent
                                    to parents until you click an athlete in.
                                </div>
                            );
                        }
                        return (
                            <div className="space-y-3">
                                <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red inline-flex items-center gap-2">
                                    <Activity className="w-3.5 h-3.5" /> Tests · {present.length} athlete{present.length === 1 ? "" : "s"} here today
                                </div>
                                {present.map((p) => (
                                    <TestSelectionPanel
                                        key={p.athlete_id}
                                        sessionId={routeId}
                                        participant={p}
                                        onUpdated={setSession}
                                    />
                                ))}
                            </div>
                        );
                    })()}

                    {/* Notes */}
                    {!isCompleted && (
                        <div>
                            <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-2">Coach notes</div>
                            <textarea
                                data-testid="session-notes"
                                rows={4}
                                value={notesDraft}
                                onChange={(e) => setNotesDraft(e.target.value)}
                                onBlur={saveNotes}
                                placeholder="Private notes — saved automatically when you click away. Won't be emailed to the participant."
                                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-brand-red"
                            />
                            <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-2">
                                {savingNotes ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                Auto-saved on blur
                            </div>
                        </div>
                    )}

                    {/* Send recap */}
                    {!isCompleted && participants.length > 0 && (() => {
                        const presentCount = participants.filter((p) => p.included_today).length;
                        return (
                            <div className="border border-zinc-800 rounded-2xl bg-zinc-950/60 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div>
                                    <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-1">Wrap up</div>
                                    <p className="text-sm text-zinc-300">
                                        Preview the recap, then send a single email per parent covering all of
                                        their athletes here today. Each email includes one <b>Pick my package</b> button.
                                    </p>
                                    <p className="text-[11px] text-zinc-500 mt-2">
                                        {presentCount === 0
                                            ? "No athletes checked in yet — nothing will send."
                                            : `${presentCount} athlete${presentCount === 1 ? "" : "s"} marked here today.`}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    data-testid="open-recap"
                                    onClick={() => { setRecapMode("send"); setShowRecap(true); }}
                                    disabled={presentCount === 0}
                                    className="btn-tactical text-xs whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <Send className="w-3.5 h-3.5" /> Preview & send recap
                                </button>
                            </div>
                        );
                    })()}
                    {isCompleted && session.status === "completed" && (
                        <div className="border border-emerald-700/60 bg-emerald-900/10 rounded-2xl px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="text-sm text-emerald-300">
                                Recap sent {session.completed_at ? `on ${new Date(session.completed_at).toLocaleString()}` : ""}
                                {session.completed_by_name ? ` by ${session.completed_by_name}` : ""}.
                                {(session.resend_log?.length || 0) > 0 && (
                                    <div className="text-[11px] text-emerald-200/70 mt-1">
                                        {session.resend_log.length} resend{session.resend_log.length === 1 ? "" : "s"} since.
                                        Last on {new Date(session.resend_log[session.resend_log.length - 1].sent_at).toLocaleString()}.
                                    </div>
                                )}
                            </div>
                            <button
                                type="button"
                                data-testid="open-resend-recap"
                                onClick={() => { setRecapMode("resend"); setShowRecap(true); }}
                                className="btn-ghost-tactical text-xs whitespace-nowrap border-emerald-700/60 text-emerald-200 hover:border-emerald-500 hover:text-emerald-100"
                            >
                                <RotateCcw className="w-3.5 h-3.5" /> Resend recap
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {showWalkIn && (
                <WalkInModal
                    sessionId={routeId}
                    onClose={() => setShowWalkIn(false)}
                    onAdded={async () => { setShowWalkIn(false); await refresh(); }}
                />
            )}
            {showRecap && (
                <AssessmentRecapModal
                    sessionId={routeId}
                    mode={recapMode}
                    onClose={() => setShowRecap(false)}
                    onSent={async () => {
                        setShowRecap(false);
                        if (recapMode === "resend") {
                            await refresh();
                        } else {
                            toast.success("Assessment complete");
                            navigate("/coach");
                        }
                    }}
                />
            )}
            {confirmCancel && (
                <ConfirmCancelModal
                    onConfirm={cancelSession}
                    onClose={() => setConfirmCancel(false)}
                />
            )}
            {liveLinkParent && (
                <ParentLiveLinkModal
                    sessionId={routeId}
                    parentUid={liveLinkParent.uid}
                    parentName={liveLinkParent.name}
                    onClose={() => setLiveLinkParent(null)}
                />
            )}
        </div>
    );
}

// -------------------------- Walk-in modal --------------------------

function WalkInModal({ sessionId, onClose, onAdded }) {
    const [isAdult, setIsAdult] = useState(false);
    const [parentFirst, setParentFirst] = useState("");
    const [parentLast, setParentLast] = useState("");
    const [parentEmail, setParentEmail] = useState("");
    const [athletes, setAthletes] = useState([{
        first_name: "", last_name: "", dob: "", grade: "", sports: [],
    }]);
    const [submitting, setSubmitting] = useState(false);

    const updateAthlete = (i, k, v) =>
        setAthletes((prev) => prev.map((a, idx) => idx === i ? { ...a, [k]: v } : a));
    const addAthlete = () =>
        setAthletes((prev) => [...prev, { first_name: "", last_name: "", dob: "", grade: "", sports: [] }]);
    const removeAthlete = (i) =>
        setAthletes((prev) => prev.filter((_, idx) => idx !== i));

    const submit = async () => {
        // Basic guard rails
        const cleanAthletes = athletes.filter((a) => a.first_name.trim() && a.last_name.trim());
        if (cleanAthletes.length === 0) {
            toast.error("Add at least one name");
            return;
        }
        if (!isAdult && !parentFirst.trim()) {
            toast.error("Parent's first name is required");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(
                `${API}/coach/assessment-sessions/${sessionId}/walkin`,
                {
                    is_adult: isAdult,
                    parent_first_name: parentFirst,
                    parent_last_name: parentLast,
                    parent_email: parentEmail,
                    athletes: cleanAthletes.map((a) => ({
                        ...a,
                        sports: a.sports || [],
                    })),
                },
                { withCredentials: true },
            );
            toast.success("Added");
            await onAdded();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg = Array.isArray(detail) ? detail[0]?.msg || "Validation error" : detail || "Couldn't add";
            toast.error("Couldn't add walk-in", { description: msg });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-xl max-h-[92vh] flex flex-col">
                <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between shrink-0">
                    <h3 className="font-display text-2xl tracking-wider uppercase">Add walk-in</h3>
                    <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-6 space-y-5 overflow-y-auto flex-1 min-h-0">
                    <div className="flex gap-2">
                        {[
                            { id: false, label: "Kid + parent" },
                            { id: true, label: "Adult (self)" },
                        ].map((opt) => (
                            <button
                                key={String(opt.id)}
                                type="button"
                                data-testid={`walkin-mode-${opt.id ? "adult" : "kid"}`}
                                onClick={() => setIsAdult(opt.id)}
                                className={`flex-1 text-xs tracking-[0.15em] uppercase px-3 py-2.5 border transition-colors ${
                                    isAdult === opt.id
                                        ? "border-brand-red bg-brand-red/10 text-brand-red"
                                        : "border-zinc-800 text-zinc-300 hover:border-zinc-600"
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>

                    {!isAdult && (
                        <>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Parent first name *">
                                    <input value={parentFirst} onChange={(e) => setParentFirst(e.target.value)} data-testid="walkin-parent-first" className={inp} />
                                </Field>
                                <Field label="Parent last name">
                                    <input value={parentLast} onChange={(e) => setParentLast(e.target.value)} data-testid="walkin-parent-last" className={inp} />
                                </Field>
                            </div>
                            <Field label="Parent email" hint="Leave blank if not available — you can add it later.">
                                <input type="email" value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} data-testid="walkin-parent-email" className={inp} />
                            </Field>
                        </>
                    )}

                    <div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-2">
                            {isAdult ? "Person being assessed" : `Athletes (${athletes.length})`}
                        </div>
                        <div className="space-y-3">
                            {athletes.map((a, i) => (
                                <div key={i} className="border border-zinc-800 rounded-lg p-3 space-y-3 bg-zinc-900/30">
                                    <div className="grid grid-cols-2 gap-3">
                                        <Field label="First name *">
                                            <input value={a.first_name} onChange={(e) => updateAthlete(i, "first_name", e.target.value)} data-testid={`walkin-athlete-first-${i}`} className={inp} />
                                        </Field>
                                        <Field label="Last name *">
                                            <input value={a.last_name} onChange={(e) => updateAthlete(i, "last_name", e.target.value)} data-testid={`walkin-athlete-last-${i}`} className={inp} />
                                        </Field>
                                        <Field label="DOB">
                                            <input type="date" value={a.dob} onChange={(e) => updateAthlete(i, "dob", e.target.value)} data-testid={`walkin-athlete-dob-${i}`} className={inp} />
                                        </Field>
                                        <Field label="Grade (optional)">
                                            <select value={a.grade} onChange={(e) => updateAthlete(i, "grade", e.target.value)} data-testid={`walkin-athlete-grade-${i}`} className={inp}>
                                                <option value="">—</option>
                                                <option value="kindergarten">Kindergarten</option>
                                                {["1st","2nd","3rd","4th","5th","6th","7th","8th","9th","10th","11th","12th"].map((g) => (
                                                    <option key={g} value={g}>{g} grade</option>
                                                ))}
                                                <option value="college">College</option>
                                            </select>
                                        </Field>
                                    </div>
                                    {!isAdult && athletes.length > 1 && (
                                        <button type="button" onClick={() => removeAthlete(i)} className="text-[11px] text-zinc-400 hover:text-brand-red inline-flex items-center gap-1">
                                            <Trash2 className="w-3 h-3" /> Remove this athlete
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        {!isAdult && (
                            <button type="button" onClick={addAthlete} data-testid="walkin-add-athlete" className="mt-3 text-xs text-brand-navy-200 hover:text-brand-red inline-flex items-center gap-1.5">
                                <UserPlus className="w-3.5 h-3.5" /> Add another athlete
                            </button>
                        )}
                    </div>
                </div>
                <div className="px-6 py-4 border-t border-zinc-800 flex justify-end gap-2 shrink-0 bg-zinc-950 rounded-b-2xl">
                    <button type="button" onClick={onClose} disabled={submitting} className="btn-ghost-tactical text-xs">Cancel</button>
                    <button type="button" onClick={submit} data-testid="walkin-submit" disabled={submitting} className="btn-tactical text-xs">
                        {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                        Add to assessment
                    </button>
                </div>
            </div>
        </div>
    );
}

function ConfirmCancelModal({ onConfirm, onClose }) {
    const [busy, setBusy] = useState(false);
    return (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-brand-red rounded-2xl w-full max-w-md p-6 space-y-4">
                <div className="inline-flex items-center gap-2 text-brand-red">
                    <AlertTriangle className="w-5 h-5" />
                    <h3 className="font-display text-2xl tracking-wider uppercase">Cancel this assessment?</h3>
                </div>
                <p className="text-sm text-zinc-300 leading-relaxed">
                    Any participants and notes will be discarded. Walk-in profiles that were created
                    stay in your roster — you'd remove those from the lead list separately if needed.
                </p>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} disabled={busy} className="btn-ghost-tactical text-xs">Keep it</button>
                    <button
                        type="button"
                        data-testid="cancel-confirm"
                        onClick={async () => { setBusy(true); await onConfirm(); }}
                        disabled={busy}
                        className="text-xs tracking-[0.15em] uppercase px-4 py-2.5 border border-brand-red bg-brand-red text-white hover:bg-brand-red/90 transition-colors inline-flex items-center gap-1.5"
                    >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        Yes, cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

// -------------------------- Form helpers --------------------------

const inp = "w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-red";

function Field({ label, hint, children }) {
    return (
        <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-300">{label}</div>
            <div className="mt-1">{children}</div>
            {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
        </div>
    );
}
